import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import swal from "sweetalert";
import { useSubheader } from "@metronic/layout";
import {
  searchByTicket,
  moveCaseStatus,
  getMovementsByCase,
} from "@services/case-manual-movs";
import { getAllCaseStatuses } from "@services/case-statuses";
import NotificationModal from "@components/shared/notification-modal";
import ManualCaseMovementModal from "./manual-case-movement-modal";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX");
  } catch (e) {
    return iso;
  }
};

const ManualCaseMovement = () => {
  const subheader = useSubheader();

  const [ticket, setTicket] = useState("");
  const [caseData, setCaseData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    title: "",
    message: "",
    variant: "warning",
  });

  useEffect(() => {
    subheader.setTitle("Gestión Tickets · Movimiento Manual de Casos");
  }, [subheader]);

  useEffect(() => {
    getAllCaseStatuses()
      .then((data) => setStatuses(data || []))
      .catch(() => setStatuses([]));
  }, []);

  const closeNotification = () =>
    setNotification((prev) => ({ ...prev, show: false }));

  const showError = (message, title = "Error") =>
    setNotification({ show: true, title, message, variant: "danger" });

  const showWarning = (message, title = "Aviso") =>
    setNotification({ show: true, title, message, variant: "warning" });

  const loadMovements = async (caseId) => {
    setLoadingMovements(true);
    try {
      const data = await getMovementsByCase(caseId);
      setMovements(data || []);
    } catch (error) {
      setMovements([]);
    } finally {
      setLoadingMovements(false);
    }
  };

  const handleSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = ticket.trim();
    if (!trimmed) {
      showWarning("Debes escribir un número de ticket");
      return;
    }

    setLoadingSearch(true);
    setCaseData(null);
    setMovements([]);
    try {
      const data = await searchByTicket(trimmed);
      setCaseData(data);
      if (data && data.id) {
        loadMovements(data.id);
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode === 404) {
        showWarning(
          `No se encontró ningún ticket con el número "${trimmed}"`,
          "Ticket no encontrado"
        );
      } else if (statusCode === 403) {
        showError(
          "No tienes el privilegio 'Gestion Tickets' para usar esta función",
          "Acceso denegado"
        );
      } else {
        showError("Ocurrió un error al buscar el ticket");
      }
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleConfirmMovement = async (payload) => {
    const response = await moveCaseStatus(payload);
    setShowMoveModal(false);
    swal(
      "Movimiento registrado",
      `El caso ${response.caseTicket} se movió de "${response.oldStatusName}" a "${response.newStatusName}".`,
      "success"
    );
    setCaseData((prev) =>
      prev
        ? {
            ...prev,
            caseStatusId: response.newStatusId,
            caseStatusName: response.newStatusName,
          }
        : prev
    );
    if (payload.caseId) {
      loadMovements(payload.caseId);
    }
  };

  const patientFullName = caseData
    ? [caseData.assuredName, caseData.assuredFlastname, caseData.assuredMlastname]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <>
      <Alert variant="info" className="text-center" style={{ fontSize: "15px" }}>
        Gestión Tickets · Movimiento Manual de Casos
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ fontSize: "16px" }}>Buscar ticket</Card.Title>
          <Card.Text className="text-muted" style={{ fontSize: "13px" }}>
            Escribe el número exacto del ticket. La búsqueda no ofrece
            sugerencias ni autocompletado.
          </Card.Text>
          <Form onSubmit={handleSearch}>
            <Row>
              <Col md={8}>
                <Form.Group controlId="ticket">
                  <Form.Control
                    type="text"
                    placeholder="Ej. ATC-000123"
                    value={ticket}
                    onChange={(e) => setTicket(e.target.value)}
                    autoComplete="off"
                    disabled={loadingSearch}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Button
                  type="submit"
                  variant="primary"
                  block
                  disabled={loadingSearch}
                >
                  {loadingSearch ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="mr-2" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-search mr-2"></i>Buscar
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {caseData && (
        <Card>
          <Card.Body>
            <Card.Title style={{ fontSize: "16px" }}>
              Detalle del ticket
            </Card.Title>
            <Row className="mt-3" style={{ fontSize: "14px" }}>
              <Col md={6} className="mb-2">
                <strong>Folio:</strong>{" "}
                <span className="text-primary">{caseData.caseTicket}</span>
              </Col>
              <Col md={6} className="mb-2">
                <strong>Tipo de caso:</strong> {caseData.caseType || "N/A"}
              </Col>
              <Col md={6} className="mb-2">
                <strong>Paciente:</strong> {patientFullName || "N/A"}
              </Col>
              <Col md={6} className="mb-2">
                <strong>Hospital:</strong> {caseData.hospitalName || "N/A"}
              </Col>
              <Col md={6} className="mb-2">
                <strong>Status actual:</strong>{" "}
                <span className="badge badge-info" style={{ fontSize: "12px" }}>
                  {caseData.caseStatusName || "Sin status"}
                </span>
              </Col>
              <Col md={6} className="mb-2">
                <strong>Creado:</strong> {formatDate(caseData.createdAt)}
              </Col>
            </Row>
            <hr />
            <div className="d-flex justify-content-end">
              <Button
                variant="warning"
                onClick={() => setShowMoveModal(true)}
                disabled={statuses.length === 0}
              >
                <i className="fa fa-exchange-alt mr-2"></i>
                Mover status
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {caseData && (
        <Card className="mt-4">
          <Card.Body>
            <Card.Title style={{ fontSize: "16px" }}>
              Historial de movimientos
            </Card.Title>
            {loadingMovements ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" className="mr-2" />
                Cargando historial...
              </div>
            ) : movements.length === 0 ? (
              <Card.Text className="text-muted" style={{ fontSize: "13px" }}>
                Este caso no tiene movimientos manuales registrados.
              </Card.Text>
            ) : (
              <Table responsive size="sm" style={{ fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Status anterior</th>
                    <th>Status nuevo</th>
                    <th>Motivo</th>
                    <th>Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => (
                    <tr key={movement.id}>
                      <td>{formatDate(movement.createdAt)}</td>
                      <td>{movement.oldStatusName || "N/A"}</td>
                      <td>{movement.newStatusName || "N/A"}</td>
                      <td>{movement.reason}</td>
                      <td>{movement.userName || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      <ManualCaseMovementModal
        show={showMoveModal}
        onHide={() => setShowMoveModal(false)}
        caseData={caseData}
        statuses={statuses}
        onConfirm={handleConfirmMovement}
      />

      <NotificationModal
        show={notification.show}
        onHide={closeNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
      />
    </>
  );
};

export default ManualCaseMovement;
